/**
 * SourceTable — discovery sources with their integrity state and fragments. Demo page: /tables/basic-tables (compact
 * rows inside a card).
 */
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { ExternalLink, Link2 } from 'lucide-react';
import { Badge, EmptyState } from '../../../components/ui/index.js';
import { Card } from '../../../components/tailgrids/core/card.js';
import { safeHref } from '../../../lib/safe-url.js';
import type { CatalogSource } from './catalog-types.js';
import {
  buildCatalogSourceEvidenceHref,
  catalogSourceFragmentLabel,
  catalogSourceFragmentMeta,
  describeCatalogSourceIntegrity,
} from './catalog-helpers.js';

export function SourceTable({ sources }: { sources: CatalogSource[] }) {
  if (sources.length === 0)
    return (
      <EmptyState icon={Link2} title="No sources" description="No daemon discovery sources have been recorded yet." />
    );
  return (
    <Card className="divide-y overflow-hidden p-0">
      {sources.map((source) => {
        const integrityState = describeCatalogSourceIntegrity(source);
        const sourceHref = buildCatalogSourceEvidenceHref(null, source);
        const fragments = source.fragments ?? [];
        return (
          <div key={source.id} className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-heading">
                {source.title ?? source.packageName ?? source.provider ?? source.canonicalKey}
              </h2>
              <Badge variant="neutral">{sentenceCase(source.sourceType)}</Badge>
              {integrityState.hasBlockingChecks && <Badge variant="danger">integrity blocked</Badge>}
              {!integrityState.hasBlockingChecks && integrityState.isSourceOnly && (
                <Badge variant="warning">source only</Badge>
              )}
              {!integrityState.hasBlockingChecks && integrityState.warningChecks > 0 && (
                <Badge variant="warning">provenance warning</Badge>
              )}
            </div>
            <div className="mt-2 text-sm text-muted break-all">{source.canonicalKey}</div>
            {integrityState.summary && (
              <div className="mt-3 rounded-sm border bg-surface-alt px-3 py-2 text-sm">
                <div className="font-medium text-heading">{integrityState.badgeLabel}</div>
                <div className="mt-1 text-muted">{integrityState.summary}</div>
                {integrityState.recommendation && (
                  <div className="mt-1 text-xs text-muted">Next step: {integrityState.recommendation}</div>
                )}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted">
              {source.provider && <span>Provider: {source.provider}</span>}
              {source.packageName && <span>Package: {source.packageName}</span>}
              {source.externalId && <span>External ID: {source.externalId}</span>}
              {fragments.length > 0 && <span>{fragments.length} fragment refs</span>}
              {source.nextRefreshAt && <span>Refresh: {new Date(source.nextRefreshAt).toLocaleDateString()}</span>}
              {sourceHref && (
                <a
                  className="inline-flex items-center gap-1 text-accent-text hover:underline"
                  href={safeHref(sourceHref) ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open source <ExternalLink size={13} />
                </a>
              )}
            </div>
            {fragments.length > 0 && (
              <details className="mt-3 rounded-sm border bg-surface-alt">
                <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-heading">
                  Source fragments
                </summary>
                <div className="divide-y border-t">
                  {fragments.slice(0, 8).map((fragment, index) => {
                    const fragmentHref = buildCatalogSourceEvidenceHref(
                      {
                        sourceFragment: fragment,
                        sourceUrl: source.sourceUrl,
                        sourceRepoUrl: source.sourceRepoUrl,
                        sourcePath: source.sourcePath,
                        sourceRef: source.sourceRef,
                      },
                      source,
                    );
                    return (
                      <div
                        key={`${fragment.fragmentType}-${fragment.sourcePath ?? fragment.sourceUrl ?? index}`}
                        className="p-3 text-xs"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-heading">{catalogSourceFragmentLabel(fragment)}</span>
                          <Badge variant="neutral" size="sm">
                            {sentenceCase(fragment.fragmentType)}
                          </Badge>
                          {fragment.classification && (
                            <Badge
                              variant={fragment.classification === 'library_candidate' ? 'success' : 'warning'}
                              size="sm"
                            >
                              {sentenceCase(fragment.classification)}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 text-muted break-all">{catalogSourceFragmentMeta(fragment)}</div>
                        <div className="mt-2 flex flex-wrap gap-3 text-muted">
                          {fragment.targetTypeHint && <span>Target: {sentenceCase(fragment.targetTypeHint)}</span>}
                          {fragment.contentHash && <span>Hash: {fragment.contentHash.slice(0, 12)}</span>}
                          {fragmentHref && (
                            <a
                              className="inline-flex items-center gap-1 text-accent-text hover:underline"
                              href={safeHref(fragmentHref) ?? undefined}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open fragment <ExternalLink size={13} />
                            </a>
                          )}
                        </div>
                        {fragment.rationale && <div className="mt-2 text-muted">{fragment.rationale}</div>}
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </Card>
  );
}
